import React, { useState, useEffect } from 'react';
import { 
  LayoutDashboard, 
  BookOpen, 
  Building2, 
  FileText, 
  Settings, 
  LogOut,
  Bell,
  Newspaper,
  MessageSquare,
  Mail,
  Menu,
  X,
  Coffee,
  Store,
  User,
  Wifi,
  WifiOff,
  Brain
} from 'lucide-react';
import { useNavigate, Link, useLocation, Outlet } from 'react-router-dom';
import { authApi } from '../api/api';
import SEO, { resetRobotsMeta } from '../components/SEO';
import logo from '../logo-optimized.jpg';

const AdminLayout = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // The stored value is attacker-controlled (anyone can edit localStorage),
    // so a malformed entry must not crash the whole admin shell.
    try {
      setCurrentUser(JSON.parse(localStorage.getItem('adminUser') || '{}'));
    } catch {
      setCurrentUser({});
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      navigate('/admin/login');
      // This layout wrote `noindex, nofollow` into <head>. Leaving it behind
      // after logout made every public page the user opened next invisible to
      // search engines until a full document reload.
      resetRobotsMeta();
    }
  };

  const sidebarLinks = [
    { name: 'Dashboard', icon: <LayoutDashboard />, path: '/admin/dashboard' },
    { name: 'Faculties', icon: <BookOpen />, path: '/admin/faculties' },
    { name: 'Departments', icon: <Building2 />, path: '/admin/departments' },
    { name: 'Facilities', icon: <Building2 />, path: '/admin/facilities' },
    { name: 'Amenities', icon: <Coffee />, path: '/admin/amenities' },
    { name: 'Business & Services', icon: <Store />, path: '/admin/business-centers' },
    { name: 'Administration', icon: <Building2 />, path: '/admin/admin-blocks' },
    { name: 'Postgraduate', icon: <BookOpen />, path: '/admin/postgraduates' },
    { name: 'Research', icon: <BookOpen />, path: '/admin/research' },
    { name: 'SCEDL', icon: <BookOpen />, path: '/admin/scedl' },
    { name: 'Documents', icon: <FileText />, path: '/admin/documents' },
    { name: 'Notices', icon: <Bell />, path: '/admin/notices' },
    { name: 'Updates', icon: <Newspaper />, path: '/admin/updates' },
    { name: 'Contact Messages', icon: <Mail />, path: '/admin/contact-messages' },
    { name: 'Staff', icon: <User />, path: '/admin/staff' },
    { name: 'AI Knowledge Sources', icon: <Brain />, path: '/admin/ai-knowledge-sources' },
    { name: 'AI Chat Logs', icon: <MessageSquare />, path: '/admin/chat-logs' },
    { name: 'Settings', icon: <Settings />, path: '/admin/settings' },
  ];

  return (
    <>
      <SEO
        title="FUDChat Admin"
        description="Private administration area for managing FUDChat university content, notices, updates, and information systems."
        robots="noindex, nofollow"
      />
      <div className="min-h-screen bg-fud-gray-light flex flex-col md:flex-row">
        {/* Mobile Header */}
      <div className="md:hidden bg-fud-dark text-white p-4 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center gap-2">
          <div className="bg-white p-0.5 rounded flex items-center justify-center">
            <img src={logo} alt="FUD Logo" className="h-5 w-5 object-contain" />
          </div>
          <span className="font-bold">FUD Admin</span>
        </div>
        <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className="p-1 hover:bg-white/10 rounded transition-colors">
          {isSidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Sidebar Overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden animate-fade-in"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 left-0 w-64 bg-fud-dark text-white flex flex-col z-50 transform transition-transform duration-300 md:translate-x-0 md:sticky md:top-0 md:h-screen
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="p-6 border-b border-white/10 hidden md:flex items-center gap-3">
          <div className="bg-white p-1 rounded-lg flex items-center justify-center">
            <img src={logo} alt="FUD Logo" className="h-6 w-6 object-contain" />
          </div>
          <span className="font-bold tracking-tight">FUDChat Admin</span>
        </div>

        {currentUser && (
          <div className="p-4 border-b border-white/10 bg-white/5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-fud-green flex items-center justify-center flex-shrink-0">
                <User className="h-5 w-5 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-bold text-white truncate">{currentUser.name || 'Admin'}</p>
                <p className="text-[10px] text-white/60 truncate">{currentUser.email}</p>
              </div>
            </div>
          </div>
        )}
        
        <nav className="flex-grow p-4 space-y-1 overflow-y-auto overscroll-contain">
          {sidebarLinks.map((link) => (
            <Link
              key={link.name}
              to={link.path}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                location.pathname === link.path
                  ? 'bg-fud-green text-white font-bold'
                  : 'text-white/60 hover:text-white hover:bg-white/5 font-medium'
              }`}
              onClick={() => setIsSidebarOpen(false)}
            >
              {React.cloneElement(link.icon, { className: "h-5 w-5" })}
              {link.name}
            </Link>
          ))}

          <div className="pt-4 mt-4 border-t border-white/10 space-y-2">
            <div className={`flex items-center gap-3 px-4 py-3 rounded-xl ${isOnline ? 'bg-fud-green/10 text-fud-green' : 'bg-red-500/10 text-red-500'} transition-all`}>
              {isOnline ? <Wifi className="h-5 w-5" /> : <WifiOff className="h-5 w-5" />}
              <div className="flex flex-col">
                <span className="text-[10px] font-black uppercase tracking-widest leading-none">Connectivity</span>
                <span className="text-xs font-bold">{isOnline ? 'Online' : 'Offline'}</span>
              </div>
            </div>

            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 text-fud-brown hover:bg-fud-brown/10 rounded-xl font-bold transition-all"
            >
              <LogOut className="h-5 w-5" />
              Logout
            </button>
          </div>
        </nav>
      </aside>

        {/* Main Content */}
        <main className="flex-grow min-w-0 w-full overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </>
  );
};

export default AdminLayout;
